import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from '@welldev-group/rcl-icon';
import Button from '@welldev-group/rcl-button';
import SelectBox from '@welldev-group/rcl-select-box';
import IconButton from '@welldev-group/rcl-icon-button';
import { I18nText } from '@welldev-group/react-comp-lib';

import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';
import selectBoxObjectMapper from '../../../shared/helper/selectBoxObjectMapper';

import TicketStatus from './TicketStatus';

const statusOptions = [
  { value: 'Resolved', label: 'Resolved' },
  { value: 'Ready', label: 'Ready' },
  { value: 'Open', label: 'Open' },
  { value: 'Submitted', label: 'Submitted' },
];

const RightPanel = props => {
  const {
    supportTicket,
    fetchTickets,
    rightPanelShow,
    setRightPanelShow,
  } = props;

  const [assignee, setAssignee] = useState(null);
  const [status, setStatus] = useState(null);
  const [editAssignee, setEditAssignee] = useState(false);
  const [editStatus, setEditStatus] = useState(null);
  const updatedAt = new Date(supportTicket.updatedAt);
  const createdOn = new Date(supportTicket.createdAt);

  const updatedAtString = updatedAt.toLocaleString(I18n.locale, {
    hour: '2-digit',
    month: 'short',
    day: 'numeric',
    minute: '2-digit',
    hour12: false,
    year: 'numeric',
  });

  const createdOnString = createdOn.toLocaleString(I18n.locale, {
    hour: '2-digit',
    month: 'short',
    day: 'numeric',
    minute: '2-digit',
    hour12: false,
    year: 'numeric',
  });

  const handleEditAssignee = () => {
    setEditAssignee(true);
  };

  const closeEditAssignee = () => {
    setEditAssignee(false);
  };

  const handleEditStatus = () => {
    setEditStatus(true);
  };

  const closeEditStatus = () => {
    setEditStatus(false);
  };

  const closeAssigneeAndStatusEdit = () => {
    setEditAssignee(false);
    setEditStatus(false);
  };

  const changeTicketState = async () => {
    const { data } = await httpClient.patch(
      routes.support.state.changeTicketState({
        support_ticket_id: supportTicket.id,
      }),
      {
        state: status.toLowerCase(),
      },
    );

    if (data.error === null) {
      fetchTickets();
      closeEditStatus();
    }
  };

  const assignTicket = async () => {
    const { data } = await httpClient.patch(
      routes.support.tickets.assign({
        support_ticket_id: supportTicket.id,
      }),
      {
        support_ticket: { assigned_to_id: assignee },
      },
    );

    if (data.error === null) {
      fetchTickets();
      closeEditAssignee();
    }
  };

  useEffect(() => {
    if (supportTicket.state) {
      setStatus(supportTicket.state);
    }
  }, [supportTicket.state]);

  useEffect(() => {
    if (supportTicket.assignedTo !== null) {
      setAssignee(supportTicket.assignedTo.id);
    } else {
      setAssignee(null);
    }
  }, [supportTicket.assignedTo]);

  useEffect(() => {
    closeAssigneeAndStatusEdit();
  }, [supportTicket.id]);

  const assignTicketButtonLabel = supportTicket.assignedTo ? (
    <I18nText id="affiliate_three_sixty.affiliate.button_label.update" />
  ) : (
    <I18nText id="affiliate_three_sixty.affiliate.button_label.assign" />
  );

  const editAssigneeSection = (
    <div className="col-12 col-grid">
      <div className="col-12 col-bleed support-ticket-card__assignee-section-header">
        <I18nText id="support.assignee" />
      </div>
      <div className="col-12 col-grid col-bleed support-ticket-card__assignee-section-name">
        <div className="col-12 col-bleed support-ticket-card__assignee-section-name__edit-select-box">
          <SelectBox
            width="full"
            options={supportTicket.assigneeOptions}
            value={selectBoxObjectMapper(
              supportTicket.assigneeOptions,
              assignee,
            )}
            touched={true}
            onChange={option => setAssignee(option ? option.value : null)}
            maxMenuHeight={220}
          />
        </div>
        <div className="col-12 col-bleed">
          <Button
            label={assignTicketButtonLabel}
            version="v2"
            size="small"
            type="primary"
            onClick={assignTicket}
          />
          <Button
            label={
              <I18nText id="affiliate_three_sixty.affiliate.button_label.cancel" />
            }
            version="v2"
            size="small"
            onClick={closeEditAssignee}
          />
        </div>
      </div>
    </div>
  );

  const assigneeDetailsSection = (
    <>
      <div className="col-11 col-md-10 col-grid">
        <div className="col-12 col-bleed support-ticket-card__assignee-section-header">
          <I18nText id="support.assignee" />
        </div>
        <div className="col-12 col-grid col-bleed support-ticket-card__assignee-section-name">
          {supportTicket.assignedTo ? (
            `${supportTicket.assignedTo.firstName} ${supportTicket.assignedTo.lastName}`
          ) : (
            <I18nText id="support.unassigned_text" />
          )}
        </div>
      </div>
      <div className="col-1 col-md-2 col-bleed-x">
        <IconButton
          version="v2"
          icon={<Icon name="edit" size="normal" />}
          onClick={handleEditAssignee}
          size="small"
        />
      </div>
    </>
  );

  const statusDetailsSection = (
    <>
      <div className="col-11 col-md-10 col-grid">
        <div className="col-12 col-bleed support-ticket-card__status-section-header">
          <I18nText id="support.status" />
        </div>
        <div className="col-12 col-bleed support-ticket-card__status-section-name">
          <TicketStatus state={supportTicket.state} emphasisTagSize="small" />
        </div>
      </div>
      <div className="col-1 col-md-2 col-bleed-x support-ticket-card__status-section--edit">
        {!editStatus && (
          <IconButton
            version="v2"
            icon={<Icon name="edit" size="normal" />}
            onClick={handleEditStatus}
            size="small"
          />
        )}
      </div>
    </>
  );

  const editStatusSection = (
    <div className="col-12 col-grid">
      <div className="col-12 col-bleed support-ticket-card__status-section-header">
        <I18nText id="support.status" />
      </div>
      <div className="col-12 col-grid col-bleed support-ticket-card__status-section-name">
        <div className="col-12 col-bleed support-ticket-card__status-section-name__edit-select-box">
          <SelectBox
            width="full"
            options={statusOptions}
            value={selectBoxObjectMapper(statusOptions, status)}
            touched={true}
            onChange={option => setStatus(option ? option.value : null)}
            maxMenuHeight={220}
            isClearable={false}
            isSearchable={false}
          />
        </div>
        <div className="col-12 col-bleed">
          <Button
            label={
              <I18nText id="affiliate_three_sixty.affiliate.button_label.update" />
            }
            version="v2"
            size="small"
            type="primary"
            onClick={changeTicketState}
          />
          <Button
            label={
              <I18nText id="affiliate_three_sixty.affiliate.button_label.cancel" />
            }
            version="v2"
            size="small"
            onClick={closeEditStatus}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="support-right-panel">
      <div className="support-right-panel__actions">
        <IconButton
          version="v2"
          icon={<Icon name="close" />}
          onClick={() => setRightPanelShow(!rightPanelShow)}
          size="tiny"
          isIconOnly={true}
        />
      </div>
      <div className="support-right-panel__ticket-info-wrapper">
        <div className="support-right-panel__ticket-info-grid">
          <div className="grid">
            <div className="col-12 support-right-panel__ticket-info-subject">
              {supportTicket.subject}
            </div>
            <div className="col-12 support-right-panel__ticket-source">
              <div className="col-12 col-bleed-y support-ticket-card__created-by">
                <I18nText id="support.created_by_user" /> -{' '}
                {supportTicket.createdBy.name}
              </div>
              <div className="col-12 col-bleed-y support-ticket-card__organization-source">
                <I18nText id="support.from_organization_source" /> -{' '}
                {supportTicket.affiliate.clientName}
              </div>
            </div>
            <div className="col-12 col-grid support-ticket-card__application-container">
              <div className="col-12 col-bleed support-ticket-card__application-header">
                <I18nText id="support.application" />
              </div>
              <div className="col-12 col-bleed support-ticket-card__application-title">
                {supportTicket.app}
              </div>
            </div>
            <div className="col-12 support-ticket-card__assignee-section">
              <div className="grid">
                {editAssignee ? editAssigneeSection : assigneeDetailsSection}
              </div>
            </div>
            <div className="col-12 support-ticket-card__status-section">
              <div className="grid">
                {editStatus ? editStatusSection : statusDetailsSection}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="support-right-panel__ticket-info-timeline-wrapper">
        <div className="support-right-panel__ticket-info-timeline-grid">
          <div className="grid">
            <div className="col-12 col-bleed-y support-right-panel__ticket-info-timeline--updated-on">
              <I18nText id="support.last_updated_on" returnStringOnly={true} />{' '}
              {updatedAtString}
            </div>
            <div className="col-12 col-bleed-y support-right-panel__ticket-info-timeline--created-on">
              <I18nText id="support.created_on" returnStringOnly={true} />{' '}
              {createdOnString}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

RightPanel.propTypes = {
  fetchTickets: PropTypes.func.isRequired,
  rightPanelShow: PropTypes.bool.isRequired,
  setRightPanelShow: PropTypes.func.isRequired,
  supportTicket: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    affiliate: PropTypes.shape({
      clientName: PropTypes.string,
    }),
    app: PropTypes.string.isRequired,
    createdBy: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
    assignedTo: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
    assigneeOptions: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
};

export default RightPanel;
